import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const HeroSlide = ({
  slide,
  onNext,
  onPrevious,
  showNavigation = true,
  showDots = true,
  currentIndex = 0,
  totalSlides = 1,
  autoplay = true,
  interval = 5000,
  onAutoplayChange, // New prop to control autoplay
  ...props
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSlideClick = () => {
    if (slide.link_url && props.onClick) {
      props.onClick(slide);
    } else if (slide.link_url && !props.onClick) {
      // Fallback: navigate using window.location if no onClick prop
      window.location.href = slide.link_url;
    }
  };

  const handlePrevious = (e) => {
    e.stopPropagation();
    if (onPrevious) onPrevious();
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (onNext) onNext();
  };

  const handleDotClick = (index) => (e) => {
    e.stopPropagation();
    if (props.onDotClick) props.onDotClick(index);
  };

  // Pause autoplay on user interaction
  const handleUserInteraction = () => {
    if (onAutoplayChange) {
      onAutoplayChange(false); // Stop autoplay when user interacts
    }
  };

  // Get appropriate image based on device
  const imageUrl = isMobile
    ? slide?.mobile_image?.hero_url || slide?.mobile_image?.url || '/api/placeholder/800/400'
    : slide?.image?.hero_url || slide?.image?.url || '/api/placeholder/1920/1080';

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 300, md: 500 },
        overflow: 'hidden',
        ...props.sx
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)',
          },
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 600, color: 'white' }}>
            <Typography
              variant={isMobile ? 'h4' : 'h2'}
              fontWeight="bold"
              gutterBottom
              sx={{
                textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                lineHeight: 1.2,
                mb: 2,
                animation: 'fadeInUp 0.8s ease-out',
              }}
            >
              {slide.title?.[i18n.language] || slide.title?.en || slide.title}
            </Typography>

            {slide.subtitle && (
              <Typography
                variant={isMobile ? 'body1' : 'h5'}
                sx={{
                  mb: 3,
                  textShadow: '1px 1px 4px rgba(0,0,0,0.8)',
                  lineHeight: 1.4,
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                }}
              >
                {slide.subtitle?.[i18n.language] || slide.subtitle?.en || slide.subtitle}
              </Typography>
            )}

            {slide.link_url && (
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                onClick={handleSlideClick}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(255,215,0,0.3)',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 25px rgba(255,215,0,0.4)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: 'fadeInUp 0.8s ease-out 0.4s both',
                }}
              >
                {t('shopNow', 'Shop Now')}
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Navigation Arrows */}
      {showNavigation && totalSlides > 1 && (
        <>
          <IconButton
            onClick={(e) => {
              handlePrevious(e);
              handleUserInteraction(); // Stop autoplay when user clicks navigation
            }}
            disabled={currentIndex === 0}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-50%) scale(1.1)',
              },
              '&:disabled': {
                opacity: 0.5,
              },
              transition: 'all 0.3s ease',
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={(e) => {
              handleNext(e);
              handleUserInteraction(); // Stop autoplay when user clicks navigation
            }}
            disabled={currentIndex === totalSlides - 1}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-50%) scale(1.1)',
              },
              '&:disabled': {
                opacity: 0.5,
              },
              transition: 'all 0.3s ease',
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}

      {/* Dots Indicator */}
      {showDots && totalSlides > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
            p: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
          }}
        >
          {[...Array(totalSlides)].map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                handleDotClick(index)(e);
                handleUserInteraction(); // Stop autoplay when user clicks dots
              }}
              sx={{
                width: index === currentIndex ? 24 : 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: index === currentIndex ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default HeroSlide;
